/**
 * Global Error Page (Next.js App Router)
 * Catches errors in the app directory
 */

'use client';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { AlertTriangle, RefreshCw, Home, ChevronLeft, Code } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function ErrorPage({ error, reset }: ErrorPageProps) {
  const router = useRouter();
  const [showDetails, setShowDetails] = useState(false);
  const isDev = process.env.NODE_ENV === 'development';

  useEffect(() => {
    // Log error for monitoring
    console.error('Global error caught:', error);
    
    // Optional: Send to error tracking service (Sentry, etc.)
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      (window as any).Sentry.captureException(error);
    }
  }, [error]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-950 to-indigo-950/70 flex items-center justify-center p-6">
      <div className="max-w-2xl w-full">
        <div className="bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          {/* Icon */}
          <div className="flex items-center justify-center mb-6">
            <div className="rounded-full bg-rose-500/20 p-4">
              <AlertTriangle className="h-12 w-12 text-rose-400" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-white text-center mb-3">
            Something went wrong
          </h1>
          <p className="text-slate-400 text-center mb-8">
            We encountered an unexpected error. Don&apos;t worry, your data is safe.
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <button
              onClick={reset}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-4 py-3',
                'rounded-xl border border-emerald-500/30 bg-emerald-500/10',
                'text-emerald-200 font-semibold',
                'hover:bg-emerald-500/20 hover:border-emerald-500/50',
                'transition-colors'
              )}
            >
              <RefreshCw className="h-4 w-4" />
              Try Again
            </button>
            <button
              onClick={() => router.push('/dashboard')}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-4 py-3',
                'rounded-xl border border-white/10 bg-white/5',
                'text-white font-semibold',
                'hover:bg-white/10 hover:border-white/20',
                'transition-colors'
              )}
            >
              <Home className="h-4 w-4" />
              Go to Dashboard
            </button>
            <button
              onClick={() => router.back()}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-4 py-3',
                'rounded-xl border border-white/10 bg-white/5',
                'text-white font-semibold',
                'hover:bg-white/10 hover:border-white/20',
                'transition-colors'
              )}
            >
              <ChevronLeft className="h-4 w-4" />
              Go Back
            </button>
          </div>

          {/* Technical Details (Dev Mode) */}
          {isDev && (
            <div className="border-t border-white/10 pt-6">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className={cn(
                  'w-full flex items-center justify-between px-4 py-2',
                  'rounded-lg border border-white/10 bg-white/5',
                  'text-slate-300 text-sm font-medium',
                  'hover:bg-white/10 transition-colors'
                )}
              >
                <span className="flex items-center gap-2">
                  <Code className="h-4 w-4" />
                  Technical Details
                </span>
                <span className="text-slate-500">
                  {showDetails ? 'Hide' : 'Show'}
                </span>
              </button>
              
              {showDetails && (
                <div className="mt-4 p-4 rounded-lg bg-slate-950/50 border border-white/5">
                  <div className="space-y-2 text-xs font-mono">
                    <div>
                      <span className="text-slate-500">Error:</span>
                      <div className="text-rose-400 mt-1 break-all">
                        {error.message}
                      </div>
                    </div>
                    {error.digest && (
                      <div>
                        <span className="text-slate-500">Digest:</span>
                        <div className="text-slate-400 mt-1">{error.digest}</div>
                      </div>
                    )}
                    {error.stack && (
                      <div>
                        <span className="text-slate-500">Stack:</span>
                        <pre className="text-slate-400 mt-1 whitespace-pre-wrap break-all text-[10px]">
                          {error.stack}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
